import React from 'react';
import { motion, Variants } from 'framer-motion';
import { ThumbsUp, ThumbsDown, Check, X } from 'lucide-react';
import { LivingInAreaData } from '../../types/livingInArea';

interface ProsConsSectionProps {
  data: LivingInAreaData['livingProsCons'];
}

// --- Animation Variants (standard Framer Motion Stagger) ---
const listContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};

const listItem: Variants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } }
};

// --- Main Component ---

export const ProsConsSection: React.FC<ProsConsSectionProps> = ({ data }) => {
  return (
    // Replaced AnimatedSection wrapper with a simple div for layout control
    <section className="max-w-7xl mx-auto px-4 space-y-12">
      {/* Section Header */}
      <div className="text-center">
        <h2 className="mb-2 text-4xl md:text-5xl font-extrabold text-indigo-600-">
          The Bottom Line
        </h2>
        <p className="text-lg text-slate-600">Honest assessment of the upsides and trade-offs</p>
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        {/* PROS Column */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          whileHover={{ scale: 1.01 }}
          // Explicit Green/Success colors
          className="rounded-[2.5rem] border border-emerald-500/40 bg-slate-900 p-8 shadow-xl transition"
        >
          <div className="mb-6 flex items-center gap-4">
            <div className="flex items-center justify-center rounded-2xl bg-green-500 p-3 text-white shadow-md">
              <ThumbsUp size={28} />
            </div>
            <h3 className="text-3xl font-bold text-emerald-400">The Good</h3>
          </div>

          <motion.ul
            variants={listContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-4"
          >
            {data.pros.map((pro, i) => (
              <motion.li key={i} variants={listItem}>
                <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:bg-emerald-50">
                  <Check className="mt-0.5 flex-shrink-0 text-green-500" size={22} />
                  <span className="font-medium text-slate-800">{pro}</span>
                </div>
              </motion.li>
            ))}
          </motion.ul>
        </motion.div>

        {/* CONS Column */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          whileHover={{ scale: 1.01 }}
          // Explicit Red/Destructive colors
          className="rounded-[2.5rem] border border-rose-500/40 bg-slate-900 p-8 shadow-xl transition"
        >
          <div className="mb-6 flex items-center gap-4">
            <div className="flex items-center justify-center rounded-2xl bg-red-500 p-3 text-white shadow-md">
              <ThumbsDown size={28} />
            </div>
            <h3 className="text-3xl font-bold text-rose-400">The Trade-offs</h3>
          </div>

          <motion.ul
            variants={listContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-4"
          >
            {data.cons.map((con, i) => (
              <motion.li key={i} variants={listItem}>
                <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:bg-rose-50">
                  <X className="mt-0.5 flex-shrink-0 text-red-500" size={22} />
                  <span className="font-medium text-slate-800">{con}</span>
                </div>
              </motion.li>
            ))}
          </motion.ul>
        </motion.div>
      </div>
    </section>
  );
};
